import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Modal, ModalProps, Switch } from "antd";
import styled from "styled-components";

import { device } from "@constants/styles";
import { colors } from "@constants/colors";
import { ButtonBlock } from "@components/Buttons";
import { useMyTranslation } from "@components/Lang";
import { recommendedResolution } from "@utils/temperatureHistory";
import { stateInterface } from "@reduxs/interface";

export interface ExportReportOptions {
  intervalMin: number;
  includeChart: boolean;
  seriesKeys: string[]; // chart series to draw in the exported chart
}

export interface ExportSeriesOption {
  key: string;
  label: string;
  color?: string;
}

type Props = {
  open: boolean;
  seriesOptions?: ExportSeriesOption[]; // selectable chart lines
  defaultSeriesKeys?: string[]; // pre-selected lines each time the modal opens
  onClose: () => void;
  onConfirm: (opts: ExportReportOptions) => void;
};

// Export-config modal for the temperature PDF: pick the reading-table
// resolution and whether to embed the chart, then download. Mirrors the
// b-coldtrack export modal.
// Crisp corner check badge marking a selected chip — one consistent indicator
// for both the resolution and chart-series pickers.
const CheckBadge = ({ inline = false }: { inline?: boolean }) => (
  <span className={`exp__check${inline ? " exp__check--inline" : ""}`}>
    <svg
      width="9"
      height="9"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#ffffff"
      strokeWidth="3.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  </span>
);

// padlock glyph on resolution options too fine for the current range (locked so
// a wide range can't build a row-exploding PDF)
const LockIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ marginLeft: 5, verticalAlign: "-2px" }}
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const ModalExportReport = ({
  open = false,
  seriesOptions = [],
  defaultSeriesKeys,
  onClose = () => {},
  onConfirm = () => {},
  ...props
}: Props & ModalProps) => {
  const { t } = useMyTranslation();
  const unit = useSelector(
    (state: stateInterface) => state.monitoring.filterDeviceDetail?.unit
  );
  // Recommended table resolution follows the period unit (hours/days→1m,
  // weeks→5m, months→30m); default to it each time the modal opens. A finer pick
  // is allowed but warned.
  const recRes = recommendedResolution(unit);
  const [interval, setInterval] = useState(recRes);
  const [includeChart, setIncludeChart] = useState(true);
  const [seriesKeys, setSeriesKeys] = useState<string[]>(
    defaultSeriesKeys ?? seriesOptions.map((o) => o.key)
  );
  useEffect(() => {
    if (open) {
      setInterval(recommendedResolution(unit));
      setSeriesKeys(defaultSeriesKeys ?? seriesOptions.map((o) => o.key));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const toggleSeries = (key: string) =>
    setSeriesKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  const tooFine = interval < recRes;

  const min = t("detail.minunit");
  const hr = t("detail.hour");
  const options: { value: number; label: string }[] = [
    { value: 1, label: `1 ${min}` },
    { value: 5, label: `5 ${min}` },
    { value: 10, label: `10 ${min}` },
    { value: 30, label: `30 ${min}` },
    { value: 60, label: `1 ${hr}` },
    { value: 120, label: `2 ${hr}` },
  ];

  return (
    <Content id="model-export-report" className="modal">
      <Modal
        title=""
        open={open}
        closable={false}
        footer={null}
        centered={true}
        getContainer={() =>
          document.getElementById("model-export-report") as HTMLElement
        }
        {...props}
      >
        <div className="modal__content">
          <div className="color-black font-sukhumvitSet-bold size-20 exp__title">
            {t("detail.reportsettings")}
          </div>

          <div className="exp__label size-14 color-gray797 font-sukhumvitSet-text">
            {t("detail.resolution")}
          </div>
          <div className="exp__grid">
            {options.map((o) => {
              // finer than the unit's floor → locked (a wide range at a fine
              // resolution builds a huge, slow PDF); shown so the options are clear
              const locked = o.value < recRes;
              return (
                <div
                  key={o.value}
                  className={`exp__opt${
                    interval === o.value ? " exp__opt--active" : ""
                  }`}
                  style={
                    locked ? { opacity: 0.4, cursor: "not-allowed" } : undefined
                  }
                  onClick={() => (locked ? null : setInterval(o.value))}
                >
                  <div className="size-16 weight-600 font-sukhumvitSet-text">
                    {o.label}
                    {locked ? <LockIcon /> : null}
                  </div>
                  {o.value === recRes ? (
                    <div className="exp__rec size-12">
                      {t("detail.recommended")}
                    </div>
                  ) : null}
                  {interval === o.value ? <CheckBadge /> : null}
                </div>
              );
            })}
          </div>
          {tooFine ? (
            <div className="exp__warn size-12 font-sukhumvitSet-text">
              {t("detail.resnotrecommend")}
            </div>
          ) : null}
          <div className="exp__note size-12 font-sukhumvitSet-text">
            {t("detail.resbreachnote")}
          </div>

          <div className="exp__toggle">
            <div className="size-14 color-black font-sukhumvitSet-text">
              {t("detail.includechart")}
            </div>
            <Switch checked={includeChart} onChange={setIncludeChart} />
          </div>
          {includeChart && seriesOptions.length > 0 ? (
            <>
              <div
                className="exp__label size-14 color-gray797 font-sukhumvitSet-text"
                style={{ marginTop: 14 }}
              >
                {t("detail.chartseries")}
              </div>
              <div className="exp__series-grid">
                {seriesOptions.map((o) => {
                  const active = seriesKeys.includes(o.key);
                  return (
                    <div
                      key={o.key}
                      className={`exp__series${
                        active ? " exp__series--active" : ""
                      }`}
                      onClick={() => toggleSeries(o.key)}
                    >
                      <span
                        className="exp__series-dot"
                        style={{ background: o.color ?? "#94a3b8" }}
                      />
                      <span className="size-12 font-sukhumvitSet-text color-black">
                        {o.label}
                      </span>
                      {active ? <CheckBadge inline /> : null}
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}

          <div className="modal__btn">
            <ButtonBlock
              text={t("confirm.cancel")}
              bgColor="#fff"
              className="modal__btn--cancel"
              onClick={onClose}
            />
            <ButtonBlock
              text={t("detail.confirmdownload")}
              className="modal__btn--cf"
              onClick={() =>
                onConfirm({ intervalMin: interval, includeChart, seriesKeys })
              }
            />
          </div>
        </div>
      </Modal>
    </Content>
  );
};

export default ModalExportReport;

const Content = styled.div`
  position: relative;
  z-index: 999999;
  width: 100%;
  max-width: 90% !important;
  & .ant-modal-content {
    border-radius: 0.833vw;
    @media ${device.desktopL} {
      border-radius: 12px;
    }
  }
  & .ant-modal {
    width: 34vw !important;
    @media ${device.desktopL} {
      width: 480px !important;
    }
  }
  & .ant-modal-mask {
    background-color: rgba(0, 0, 0, 0.7) !important;
  }
  .exp {
    &__title {
      text-align: left;
      margin-bottom: 1.25vw;
      @media ${device.desktopL} {
        margin-bottom: 18px;
      }
    }
    &__label {
      text-align: left;
      margin-bottom: 0.556vw;
      @media ${device.desktopL} {
        margin-bottom: 8px;
      }
    }
    &__grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 0.556vw;
      @media ${device.desktopL} {
        gap: 8px;
      }
    }
    &__opt {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 0.694vw;
      text-align: center;
      cursor: pointer;
      @media ${device.desktopL} {
        padding: 10px;
      }
      &--active {
        border-color: #febc11;
        background: #fff8e8;
      }
    }
    &__check {
      position: absolute;
      top: 8px;
      right: 8px;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #d4880b;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    &__check--inline {
      position: static;
      margin-left: auto;
    }
    &__rec {
      color: #d4880b;
      margin-top: 2px;
    }
    &__warn {
      text-align: left;
      margin: 10px 0 0;
      padding: 8px 10px;
      border-radius: 6px;
      background: #fff4e5;
      border: 1px solid #ffd8a8;
      color: #b26a00;
      line-height: 1.45;
    }
    &__note {
      text-align: left;
      margin: 8px 0 0;
      color: #64748b;
      line-height: 1.45;
    }
    &__toggle {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin: 1.25vw 0 0;
      @media ${device.desktopL} {
        margin: 18px 0 0;
      }
      /* amber "on" state to match the filter theme (antd defaults to blue) */
      & .ant-switch-checked {
        background: #febc11;
      }
    }
    &__series-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
    }
    &__series {
      position: relative;
      display: flex;
      align-items: center;
      gap: 6px;
      text-align: left;
      border: 1.5px solid #e2e8f0;
      border-radius: 8px;
      padding: 6px 10px;
      cursor: pointer;
      opacity: 0.5;
      transition: opacity 0.15s, background 0.15s;
      &--active {
        border-color: #febc11;
        background: #fff8e8;
        opacity: 1;
        font-weight: 600;
      }
    }
    &__series-dot {
      flex: none;
      width: 10px;
      height: 10px;
      border-radius: 3px;
    }
  }
  .modal {
    &__content {
      text-align: center;
    }
    &__btn {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      margin: 1.667vw 0 0;
      &--cancel {
        color: ${colors.ppDark};
        border: 0.139vw solid ${colors.ppDark};
        width: 38%;
        font-family: "SukhumvitSet-Bold", sans-serif;
      }
      &--cf {
        width: 58%;
        font-family: "SukhumvitSet-Bold", sans-serif;
      }
      @media ${device.desktopL} {
        margin: 24px 0 0;
        &--cancel {
          border: 2px solid ${colors.ppDark};
        }
      }
    }
  }
`;
